import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { DwComponent, DwComponentType, DwContextSubjects, DwMdFormContextInfo, DwMetaDataFormConfig,
  DwMetaDataService, DwMetaDataServiceToken, DwOrmDataService, DwOrmDataServiceToken,
  UserMdItemSecurity, DwMediaModalService, DwMediaConfig, DwMediaConfigToken,
  DwModalService, DwModalConfig, DwModalSize, DwModalButtonStandardConfigs } from '@devwareapps/devware-cap';
import { combineLatest, defer, Observable, of, Subject, Subscription } from 'rxjs';
import { finalize, switchMap, take, takeUntil } from 'rxjs/operators';
import { AppMetaData, RecipientEntity, RecipientUpdateEntity, RecipientPhotoEntity, RecipientUpdatePhotoEntity } from '../../../meta-data/app-meta-data.service';
import { RecipientPreviewComponent } from './recipient-preview.component';
import { RecipientPreviewService } from '../services/recipient-preview.service';
import { cardPosition, recipientCardImageStyle } from './recipient-photo-framing';

type Photo = RecipientPhotoEntity & RecipientUpdatePhotoEntity;

@DwComponent({ key: 'fitness-recipient-photo-gallery', name: 'Recipient photos', componentType: DwComponentType.formSection, isGlobal: true })
@Component({ selector: 'app-recipient-photo-gallery', templateUrl: './recipient-photo-gallery.component.html',
  styleUrls: ['./recipient-photo-gallery.component.scss'] })
export class RecipientPhotoGalleryComponent implements OnInit, OnDestroy {
  readonly cardStyle = recipientCardImageStyle;
  position = cardPosition;
  @Input() formConfig: DwMetaDataFormConfig;
  @Input() sectionConfig: { config?: { updates?: boolean } };
  @Input() set context(context: DwContextSubjects) {
    this.contextSubscription.unsubscribe(); this.contextSubscription = new Subscription();
    if (!context?.data || !context?.mdFormContext) { return; }
    this.contextSubscription.add(combineLatest([context.data, context.mdFormContext]).subscribe(([data, form]) => {
      this.formContext = form;
      this.parentData = data;
      this.syncParent();
    }));
  }
  parentId = 0;
  rows: Photo[] = [];
  loading = false;
  busy = false;
  uncertain = false;
  error = '';
  message = '';
  permissions: Partial<UserMdItemSecurity> = {};
  private ready = false;
  private formContext: DwMdFormContextInfo;
  private parentData: RecipientEntity & RecipientUpdateEntity;
  private parentKey = '';
  private contextSubscription = new Subscription();
  private loadSubscription = new Subscription();
  private destroyed = new Subject<void>();
  constructor(@Inject(DwOrmDataServiceToken) private orm: DwOrmDataService,
    @Inject(DwMetaDataServiceToken) private metadata: DwMetaDataService,
    private dialogs: DwMediaModalService, @Inject(DwMediaConfigToken) private mediaConfig: DwMediaConfig,
    private modal: DwModalService, public previewRepository: RecipientPreviewService) {}
  preview(): void {
    if (this.updates || !this.parentId || !this.formContext?.loadDataComplete || this.busy || !this.previewRepository.allowed) { return; }
    const config = new DwModalConfig<{ recipientId: number }>();
    config.title = 'Recipient preview'; config.component = RecipientPreviewComponent;
    config.data = { recipientId: this.parentId }; config.cloneData = false;
    config.modalSize = DwModalSize.custom;
    config.customModalWidth = 'min(1200px, calc(100vw - 24px))'; config.customModalHeight = 'auto';
    config.buttons = [{ ...DwModalButtonStandardConfigs.instance.closeButton }];
    this.contextSubscription.add(this.modal.showModal(config).pipe(take(1)).subscribe());
  }
  get updates(): boolean { return this.sectionConfig?.config?.updates === true || this.formConfig?.rootItemName === AppMetaData.RecipientUpdate.ItemDetail.itemName; }
  get itemName(): string { return this.updates ? AppMetaData.RecipientUpdatePhoto.ItemDetail.itemName : AppMetaData.RecipientPhoto.ItemDetail.itemName; }
  get editable(): boolean { return !!this.parentId && this.formContext?.loadDataComplete && !this.formContext.isReadOnly &&
    !this.formConfig?.readOnly && ['edit', 'addEdit'].includes(this.formContext.contextMode); }
  get locked(): boolean { return !this.editable || this.loading || this.busy || this.uncertain; }
  id(row: Photo): number { return this.updates ? row.RecipientUpdatePhotoId : row.RecipientPhotoId; }
  // CAP assigns context before sectionConfig/formConfig when creating dynamic sections.
  setupComponentComplete(): void { this.syncParent(); }
  private syncParent(): void {
    const id = this.formContext?.loadDataComplete ? Number(this.updates ? this.parentData?.RecipientUpdateId : this.parentData?.RecipientId) || 0 : 0;
    const key = this.itemName + ':' + id;
    if (key !== this.parentKey) { this.parentKey = key; this.parentId = id; this.rows = []; this.load(); }
  }
  ngOnInit(): void {
    this.metadata.getUserMetaDataItemSecurity(this.itemName).pipe(take(1), takeUntil(this.destroyed)).subscribe(permissions => {
      this.permissions = permissions; this.ready = true; this.load();
    }, () => this.error = 'Unable to load photo permissions. Refresh the form.');
  }
  load(): void {
    this.loadSubscription.unsubscribe(); this.loadSubscription = new Subscription();
    if (!this.ready || !this.parentId) { this.loading = false; return; }
    const parentId = this.parentId;
    this.loading = true; this.error = '';
    const query: Observable<Photo[]> = this.updates
      ? this.orm.executeQuery<RecipientUpdatePhotoEntity>(AppMetaData.RecipientUpdatePhoto.CreateQueryBuilder()
        .addFilterAnd(f => f.Equal(a => a.RecipientUpdateId, parentId)).addOrderBy(a => a.DisplayOrder).addOrderBy(a => a.RecipientUpdatePhotoId))
      : this.orm.executeQuery<RecipientPhotoEntity>(AppMetaData.RecipientPhoto.CreateQueryBuilder()
        .addFilterAnd(f => f.Equal(a => a.RecipientId, parentId)).addOrderBy(a => a.DisplayOrder).addOrderBy(a => a.RecipientPhotoId));
    this.loadSubscription.add(query.pipe(take(1)).subscribe(rows => {
      if (parentId === this.parentId) { this.rows = rows; this.loading = false; this.uncertain = false; }
    }, () => { this.loading = false; this.uncertain = true; this.error = 'Unable to load photos. Try again.'; }));
  }
  upload(): void {
    if (this.locked || !this.permissions.CanCreate) { return; }
    const parentId = this.parentId;
    this.run(() => this.dialogs.showSelectFileModal({
      allowedFileTypes: this.mediaConfig.webImageFileExtensions || 'jpg,jpeg,png,gif,webp', isSecure: false,
      parentItemName: this.itemName, parentAttributeName: this.updates
        ? AppMetaData.RecipientUpdatePhoto.Attributes.MediaId.fieldName : AppMetaData.RecipientPhoto.Attributes.MediaId.fieldName,
      uploadInstructions: 'Choose a recipient photo. Only published stories display photos publicly.',
    }).pipe(take(1), switchMap(media => {
      if (!media) { return of(null); }
      if (parentId !== this.parentId || !this.editable) { throw new Error('The form changed. Refresh photos.'); }
      if (!Number.isSafeInteger(Number(media.MediaId)) || Number(media.MediaId) <= 0) { throw new Error('No photo was returned.'); }
      return this.save(this.entity(0, { MediaId: media.MediaId, DisplayOrder: this.rows.reduce((n, p) => Math.max(n, p.DisplayOrder || 0), 0) + 1,
        IsDefault: this.rows.length === 0 }));
    })), 'Photo added.');
  }
  saveText(row: Photo, caption: string, alt: string): void {
    if (this.locked || !this.permissions.CanUpdate) { return; }
    this.run(() => this.save(this.entity(this.id(row), { Caption: caption.trim() || null, AltText: alt.trim() || null })), 'Photo details saved.');
  }
  saveFraming(row: Photo): void {
    if (this.updates || this.locked || !this.permissions.CanUpdate) { return; }
    this.run(() => this.save(this.entity(this.id(row), {
      CardFitWholePhoto: row.CardFitWholePhoto === true,
      CardPositionXPercent: cardPosition(row.CardPositionXPercent, 50),
      CardPositionYPercent: cardPosition(row.CardPositionYPercent, 10),
    })), 'Card framing saved.');
  }
  setDefault(row: Photo): void {
    if (this.locked || !this.permissions.CanUpdate || row.IsDefault) { return; }
    // Clear the previous default first: SQL enforces uniqueness during every statement.
    // A failed second save leaves no default; a refresh makes that state explicit.
    const previous = this.rows.find(p => p.IsDefault);
    this.run(() => (previous ? this.save(this.entity(this.id(previous), { IsDefault: false })) : of(null)).pipe(
      switchMap(() => this.save(this.entity(this.id(row), { IsDefault: true })))), 'Default photo saved.');
  }
  move(index: number, offset: number): void {
    if (this.locked || !this.permissions.CanUpdate || index + offset < 0 || index + offset >= this.rows.length) { return; }
    const rows = this.rows.slice(); const [row] = rows.splice(index, 1); rows.splice(index + offset, 0, row);
    this.run(() => this.orm.saveEntityCollection(rows.map((p, i) => this.entity(this.id(p), { DisplayOrder: i + 1 }))), 'Photo order saved.');
  }
  remove(row: Photo): void {
    if (this.locked || !this.permissions.CanDelete) { return; }
    this.run(() => this.dialogs.showRemoveFileConfigDialog({ showConfirmDialog: true, useConfirmDefaults: false,
      title: 'Remove photo', message: 'Remove this photo from the story? The uploaded file will remain available.' }).pipe(take(1),
      switchMap(confirmed => confirmed ? this.orm.deleteEntity(this.entity(this.id(row), {})) : of(null))), 'Photo removed.');
  }
  private entity(id: number, values: Partial<Photo>): Photo {
    if (this.updates) {
      return Object.assign(AppMetaData.RecipientUpdatePhoto.CreateEntity(), id ? { RecipientUpdatePhotoId: id } : { RecipientUpdateId: this.parentId }, values);
    }
    return Object.assign(AppMetaData.RecipientPhoto.CreateEntity(), id ? { RecipientPhotoId: id } : { RecipientId: this.parentId }, values);
  }
  private save(photo: Photo): Observable<Photo> { return this.orm.saveEntity(photo); }
  private run<T>(operation: () => Observable<T>, message: string): void {
    if (this.locked) { return; }
    const parentId = this.parentId;
    this.busy = true; this.error = ''; this.message = '';
    defer(operation).pipe(take(1), takeUntil(this.destroyed), finalize(() => this.busy = false)).subscribe(result => {
      if (parentId === this.parentId && result != null) { this.message = message; this.load(); }
    }, () => { if (parentId === this.parentId) { this.uncertain = true; this.error = 'Unable to finish saving photos. Refresh photos to check the result before trying again.'; } });
  }
  ngOnDestroy(): void { this.destroyed.next(); this.destroyed.complete(); this.contextSubscription.unsubscribe(); this.loadSubscription.unsubscribe(); }
}
